import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { PageHeader } from '../../components/PageHeader';
import { Spinner } from '../../components/Spinner';
import { DOCUMENT_TYPE_LABEL, type DocumentDoc, type UserDoc } from '../../lib/types';
import { DEMO_DOCUMENTS, lesseeByUid } from '../../lib/demoData';

export function AdminVerifyDocs() {
  const { fbUser, demoMode } = useAuth();
  const [docs, setDocs] = useState<DocumentDoc[] | null>(null);

  useEffect(() => {
    if (demoMode) {
      setDocs(DEMO_DOCUMENTS.filter((d) => d.verificationStatus === 'pending'));
      return;
    }
    if (!db) return;
    const unsub = onSnapshot(
      query(
        collection(db, 'documents'),
        where('verificationStatus', '==', 'pending'),
        orderBy('uploadedAt', 'asc')
      ),
      (snap) => setDocs(snap.docs.map((d) => ({ ...(d.data() as DocumentDoc), documentId: d.id })))
    );
    return () => unsub();
  }, [demoMode]);

  // Who uploaded each document. The demo's pilots are sample data; live, each
  // uploader's user record is read once (the owner can read every user).
  const [people, setPeople] = useState<Record<string, UserDoc | null>>({});
  useEffect(() => {
    if (demoMode || !db || !docs) return;
    const missing = [...new Set(docs.map((d) => d.ownerUid))].filter((uid) => !(uid in people));
    if (!missing.length) return;
    let live = true;
    Promise.all(
      missing.map((uid) =>
        getDoc(doc(db!, 'users', uid))
          .then((snap) => [uid, snap.exists() ? (snap.data() as UserDoc) : null] as const)
          .catch(() => [uid, null] as const)
      )
    ).then((rows) => live && setPeople((cur) => ({ ...cur, ...Object.fromEntries(rows) })));
    return () => {
      live = false;
    };
  }, [demoMode, docs, people]);

  async function verify(d: DocumentDoc) {
    if (demoMode) {
      setDocs((curr) => (curr ?? []).filter((x) => x.documentId !== d.documentId));
      return;
    }
    if (!db || !fbUser) return;
    await updateDoc(doc(db, 'documents', d.documentId), {
      verificationStatus: 'verified',
      verifiedAt: serverTimestamp(),
      verifiedBy: fbUser.uid,
    });
  }

  async function reject(d: DocumentDoc) {
    const reason = prompt('Reason for rejection (shown to lessee):');
    if (!reason) return;
    if (demoMode) {
      setDocs((curr) => (curr ?? []).filter((x) => x.documentId !== d.documentId));
      return;
    }
    if (!db) return;
    await updateDoc(doc(db, 'documents', d.documentId), {
      verificationStatus: 'rejected',
      rejectionReason: reason,
    });
  }

  return (
    <>
      <PageHeader title="Verify documents" subtitle="Approve or reject pending uploads." />
      {!docs ? (
        <Spinner label="Loading documents" />
      ) : docs.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted">No pending documents.</div>
      ) : (
        <div className="space-y-3">
          {docs.map((d) => {
            const u = demoMode ? lesseeByUid(d.ownerUid) : people[d.ownerUid];
            return (
              <div key={d.documentId} className="card p-4 sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-display text-base font-semibold text-navy-900">
                      {u && (u.personal?.firstName || u.personal?.lastName || u.email) ? (
                        <>
                          {u.personal?.firstName || u.personal?.lastName
                            ? `${u.personal?.firstName ?? ''} ${u.personal?.lastName ?? ''}`.trim()
                            : u.email}
                        </>
                      ) : (
                        <span className="font-mono text-sm">{d.ownerUid.slice(0, 8)}…</span>
                      )}
                    </div>
                    <div className="mt-0.5 text-sm text-ink-muted">{DOCUMENT_TYPE_LABEL[d.type]}</div>
                  </div>
                  <a
                    href={d.fileRef}
                    target="_blank"
                    rel="noopener"
                    className="text-sm font-medium text-navy-700 underline"
                  >
                    Open file
                  </a>
                </div>
                <div className="mt-3 flex flex-wrap items-baseline gap-x-2 text-sm">
                  <span className="text-ink-muted">Expires</span>
                  <span className="text-ink">{d.expirationDate || '—'}</span>
                </div>
                <div className="mt-3 flex gap-2 border-t border-slate-100 pt-3">
                  <button onClick={() => verify(d)} className="btn-primary py-1.5 px-3 text-xs min-h-0">
                    Verify
                  </button>
                  <button onClick={() => reject(d)} className="btn-danger py-1.5 px-3 text-xs min-h-0">
                    Reject
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
